import { AmplitudeEvent, AuthType } from '@Praia-Health/spi-amplitude-client/dist/types/amplitudeConfig';
import AmplitudeBrowserClient from '@Praia-Health/spi-amplitude-client/dist/amplitudeBrowserClient';
import { AmplitudeClientConfig } from '@Praia-Health/spi-amplitude-client/dist/types/amplitudeConfig';
import { AmplitudeIdentifiers } from '../../clientConfig';
import { getClientCorrelationId } from '../logger/logMetadata';
import { getDeviceType } from '../util/clientContextUtils';

let ampClient: AmplitudeBrowserClient;
let brand: string;
let isMobile: boolean;
let userId: string;

export enum AppPromotionEvents {
    VIEW_APP_PROMOTION = 'SSO - App Promo - Shown',
    SKIP_APP_PROMOTION = 'SSO - App Promo - Skip',
    DOWNLOAD_APP_FROM_PROMOTION = 'SSO - App Promo - Download App',
}

export const initAmplitude = (config: AmplitudeClientConfig, brandInput: string, isMob: boolean, customDeviceId?: string, customSessionId?: string) => {
    brand = brandInput;
    isMobile = isMob;
    ampClient = AmplitudeBrowserClient.getInstance(config, customDeviceId, customSessionId);
};

export const getAmplitudeIdentifiers = () => {
    const browserClient = ampClient.amplitudeClient as unknown as {
        getDeviceId: () => string;
        getSessionId: () => number;
    };
    return {
        amplitudeDeviceId: browserClient.getDeviceId(),
        amplitudeSessionId: browserClient.getSessionId(),
    } as AmplitudeIdentifiers;
};

export const setUserId = (accountId: string) => {
    userId = accountId;
};

export type EventProperties = { [key: string]: string };

type SendEventOptions = {
    eventType: string;
    jwt?: string;
    eventProperties?: EventProperties;
};

export const sendEventByOptions = (options: SendEventOptions) => {
    const { eventType, jwt, eventProperties } = options;

    const eventPropsData: AmplitudeEvent = {
        event_type: eventType,
    };

    if (eventProperties) {
        eventPropsData.event_properties = { ...eventProperties, correlationId: getClientCorrelationId() || '' };
    }
    sendEvent(eventPropsData, jwt);
};

export const sendEventByType = (eventType: string, jwt?: string) => {
    const eventPropsData: AmplitudeEvent = {
        event_type: eventType,
    };
    sendEvent(eventPropsData, jwt);
};

export const sendEvent = (eventPropsData: AmplitudeEvent, jwt?: string) => {
    if (jwt) {
        eventPropsData.user_id ??= userId;
    }
    const authType = jwt ? AuthType.JWT : AuthType.NoAuth;
    ampClient.trackEvent(decorateSiteInfo(eventPropsData), authType, jwt);
};

export const decorateSiteInfo = (event: AmplitudeEvent): AmplitudeEvent => {
    const eventPropsWithSiteInfo = {
        ...event.event_properties,
        correlationId: getClientCorrelationId() || '',
        referring_site_domain: isMobile ? `cep_${brand}` : window.location.hostname,
        referring_site_url: isMobile ? '' : window.location.hostname + window.location.pathname,
    };
    return { ...event, event_properties: eventPropsWithSiteInfo };
};

export function buildIdVerificationEventProps(eventType: string, identityVerification: string) {
    const eventPropsData: AmplitudeEvent = {
        event_type: eventType,
        user_id: userId,
        event_properties: {
            identity_verification: identityVerification,
        },
    };
    return eventPropsData;
}

export function buildPhoneNumberEventProps(eventType: string, identityVerification: string, phoneNumber: string) {
    const eventPropsData: AmplitudeEvent = {
        event_type: eventType,
        user_id: userId,
        event_properties: {
            identity_verification: identityVerification,
            phone_number: phoneNumber,
        },
    };
    return eventPropsData;
}

export function buildAIAEventProps(eventType: string) {
    const eventPropsData: AmplitudeEvent = {
        event_type: eventType,
        user_id: userId,
        event_properties: {
            correlationId: getClientCorrelationId() || '',
            activation_method: 'AIA',
            referring_site_domain: window.location.hostname,
        },
    };
    return eventPropsData;
}

export function buildAppPromotionEventProps(eventType: string, brandName: string, activation_method = 'AIA') {
    const eventPropsData: AmplitudeEvent = {
        event_type: eventType,
        user_id: userId,
        event_properties: {
            correlationId: getClientCorrelationId() || '',
            activation_method: activation_method,
            referring_site_domain: window.location.hostname,
            promo_creative: getDeviceType(),
            brand: brandName,
        },
    };
    return eventPropsData;
}
